import _ from 'lodash'
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import * as graph from '@adc/sdk'
import { Grid, Button, Drawer } from '@adc/ui-adc'
import classnames from 'classnames/bind'
import Header from '../header'
import AssetFilter from '../assetFilter'
import AssetsNavigation from '../assetsNavigation'
import AssetsPagination from '../assetsPagination'
import AssetStatusFiltering from '../assetStatusFiltering'
import AuctionDates from '../auctionDates'
import AssetsResults from '../../containers/assetsResults'
import ProcessingMessageWrapper from '../event.processingMessageWrapper'
import Venues from '../venues'
import { getDefaultFilters } from '../../utils/event.filters'
import STATICS from '../../event.statics'
import types from '../../constants/event.propTypes'
import styles from './styles.scss'
const { Row, Column } = Grid
const cx = classnames.bind(styles)
const rootElmId = 'event_details'

const { ListingStatus, EventType } = graph.enums

class EventPanel extends Component {
  static propTypes = {
    event: PropTypes.object.isRequired,
    listings: PropTypes.object.isRequired,
    total: PropTypes.number.isRequired,
    offset: PropTypes.number.isRequired,
    fetchAssetsAction: PropTypes.func.isRequired,
    fetchAssetsProgress: types.baseProgressType,
    fetchAuctionInventoryAction: PropTypes.func.isRequired,
    featureFlags: PropTypes.object.isRequired,
  }

  static defaultProps = {
    fetchAssetsProgress: null,
  }

  constructor(props) {
    super(props)
    const { event, featureFlags } = props

    // TODO: Discuss with product allowing a more granular search here for matching on the county and state. This
    // usually isn't an issue other than the dreaded coming soon event.
    const venueId = _.get(event, 'query.selected_venue_id')
    const venueDate = _.get(event, 'query.venue_by_date')

    this.state = {
      openFilters: false,
      fetchParams: {
        offset: 0,
        limit: featureFlags['fe.resi.event.infinite-scroll-configuration']
          ?.enabled
          ? featureFlags['fe.resi.event.infinite-scroll-configuration']
              ?.batchSize
          : STATICS.PAGE_SIZE[0].value,
        auction_number: event.event_code,
        // auction_id: event.auction_id,
        include_test_event: 'y',
        // TODO: Morpheus does not store dates this way but resi-event has always supported events coming in the yyyy-mm-dd
        // format. We don't want to tackle changing the query params interface so we need to keep accepting those date formats
        // Filters results based on query string
        auctionDate: venueDate
          ? moment(venueDate, 'YYYY-MM-DD').format(moment.HTML5_FMT.DATE)
          : undefined,
        // NOTE: This data is indexed as a long so we convert once here to stop having to do string conversions
        // everywhere else
        venues: venueId ? [venueId] : undefined,
        available: venueId ? 'y' : undefined,
        fields: 'product_type,auction_type,status,featured,pre_sale,available',
        sort: 'resi_sort_v2',
        filters: getDefaultFilters(),
      },
    }
  }

  componentDidMount() {
    this.fetchAssets({})
  }

  fetchAssets = params => {
    const fetchParams = {
      ...this.state.fetchParams,
      ...params,
    }

    // reset the pagination, unless we're switching pages
    if (!params.offset) {
      fetchParams.offset = 0
    }

    // check if state or county have changed to update filters and store
    if (
      fetchParams.filters.stateFilter.value !==
      this.state.fetchParams.filters.stateFilter.value
    ) {
      fetchParams.filters.countyFilter = STATICS.DEFAULT_COUNTY_FILTER
      fetchParams.filters.cityFilter = STATICS.DEFAULT_CITY_FILTER
    } else if (
      fetchParams.filters.countyFilter.value !==
      this.state.fetchParams.filters.countyFilter.value
    ) {
      fetchParams.filters.cityFilter = STATICS.DEFAULT_CITY_FILTER
    }

    this.setState({ fetchParams })
    this.props.fetchAssetsAction(fetchParams)
  }

  closeFilters = () => {
    this.setState({
      openFilters: false,
    })
  }

  openFilters = () => {
    this.setState({
      openFilters: true,
    })
  }

  render() {
    const {
      event,
      listings,
      total: totalAssets,
      offset,
      fetchAssetsProgress,
      fetchAuctionInventoryAction,
      featureFlags,
    } = this.props

    const { title: headerSubtitle, trustee_sale: isTrustee } = event

    const isOnline =
      event.event_configuration?.present_as_online_event ||
      event.event_type === EventType.Online

    const { fetchParams, openFilters } = this.state
    const assetsLoading = fetchAssetsProgress.loading

    const { listing_status: aggregation } = listings.aggregation

    // NOTE: Existing logic also didn't add up to the totals correctly here. You
    // should not expect the tabs to sum to the total since some listyings are
    // available in `all` but don't land in a category for the other tabs.
    const counts = {
      allAssets: listings.total,
      availableAssets: _.sum([
        aggregation[ListingStatus.SalePending],
        aggregation[ListingStatus.SalePendingCleared],
        aggregation[ListingStatus.AuctionInProgress],
      ]),
      cancelledAssets: _.sum([aggregation[ListingStatus.Canceled]]),
      postponedAssets: _.sum([
        aggregation[ListingStatus.Postponed],
        aggregation[ListingStatus.PostponedPendingScheduling],
      ]),
    }

    const currentLocationAssetFilters = {
      stateList: listings.aggregation.location.states,
      countyList: !fetchParams.filters.stateFilter.value
        ? []
        : listings.aggregation.location.counties,
      cityList: !fetchParams.filters.countyFilter.value
        ? []
        : listings.aggregation.location.cities,
    }

    const auctionDates = (
      <Row>
        <Column>
          <AuctionDates
            elmId={rootElmId}
            dates={event.__computed__.venue_dates}
            selected={fetchParams.auctionDate}
            onSelectDate={auctionDate => this.fetchAssets({ auctionDate })}
          />
        </Column>
      </Row>
    )

    const LocalAssetStatusFiltering = ({ elmId, className }) => (
      <Row>
        <Column className={cx('u-mb-4')}>
          <AssetStatusFiltering
            elmId={elmId}
            className={className}
            allAssets={counts.allAssets}
            availableAssets={counts.availableAssets || 0}
            postponedAssets={counts.postponedAssets || 0}
            cancelledAssets={counts.cancelledAssets || 0}
            fetchAssets={this.fetchAssets}
            data={fetchParams}
            assetsLoading={assetsLoading}
          />
        </Column>
      </Row>
    )

    return (
      <section className={cx('u-mb-5')}>
        <Grid container>
          <Row>
            <Column>
              <Header
                elmId={rootElmId}
                title={event.__computed__.page_title}
                subtitle={headerSubtitle}
              />
            </Column>
          </Row>

          <Row className={cx('container')}>
            <Column computer={4} className={cx('desktop-asset-filter')}>
              <AssetFilter
                elmId={rootElmId}
                onChangeHandler={filters => this.fetchAssets({ filters })}
                filters={fetchParams.filters}
                {...currentLocationAssetFilters}
              />
            </Column>
            <Column computer={12} tablet={16}>
              {!isOnline && auctionDates}

              <Row>
                <Column>
                  <Venues
                    elmId={rootElmId}
                    venues={event.venues}
                    event={event}
                    isOnline={isOnline}
                    filteredDate={fetchParams.auctionDate}
                    selected={fetchParams.venues}
                    onChangeVenues={venues => this.fetchAssets({ venues })}
                  />
                </Column>
              </Row>

              <Row>
                <Column
                  tablet={4}
                  className={cx('show-on-computer-down', 'align-right')}
                >
                  <Button
                    role='secondary'
                    elmId={`${rootElmId}_show_filters`}
                    onClick={this.openFilters}
                    className={cx('mobile-filter-button')}
                  >
                    Show Filters
                  </Button>
                </Column>
              </Row>

              {isTrustee && (
                <LocalAssetStatusFiltering
                  elmId='desktop'
                  className={cx('hide-on-tablet')}
                />
              )}

              <Row className='u-mb-2'>
                <Column tablet={4} className={cx('hide-on-tablet')}>
                  {!isTrustee && !assetsLoading && (
                    <h2
                      className={cx('navigation-container-element')}
                      data-elm-id='asset_total_reo_desktop'
                    >
                      {totalAssets} Listings
                    </h2>
                  )}
                </Column>
              </Row>

              <Row>
                <Column computer={12} className={cx('u-mb-3')}>
                  <AssetsNavigation
                    elmId={rootElmId}
                    fetchAssets={this.fetchAssets}
                    downloadInventory={() =>
                      fetchAuctionInventoryAction(
                        event.event_code,
                        Boolean(
                          featureFlags['fe.resi.enable-credit-bid-reveal']
                        )
                      )
                    }
                  />
                </Column>
              </Row>

              {!isTrustee && !assetsLoading && (
                <Row
                  className={cx(
                    'show-on-tablet',
                    'dont-show-until-override',
                    'u-mb-2'
                  )}
                >
                  <Column>
                    <h2
                      data-elm-id='asset_total_reo_mobile'
                      className={cx('total-assets-label')}
                    >
                      {totalAssets} Listings
                    </h2>
                  </Column>
                </Row>
              )}

              {isTrustee && (
                <LocalAssetStatusFiltering
                  elmId='mobile'
                  className={cx('show-on-tablet', 'dont-show-until-override')}
                />
              )}

              <Row>
                <Column>
                  {assetsLoading ? (
                    <ProcessingMessageWrapper
                      message='Fetching Event Assets'
                      elmId='event_assets_table_processing_message'
                    />
                  ) : (
                    <AssetsResults />
                  )}
                </Column>
              </Row>

              {!assetsLoading && (
                <Row>
                  <Column>
                    <AssetsPagination
                      total={totalAssets}
                      offset={offset}
                      limit={fetchParams.limit}
                      fetchAssets={this.fetchAssets}
                    />
                  </Column>
                </Row>
              )}
            </Column>
          </Row>

          <div className={cx('show-on-tablet', 'dont-show-until-override')}>
            <Drawer
              open={openFilters}
              onClose={this.closeFilters}
              title='Filters'
              infiniteScroll
            >
              <div className={cx('drawer-content')}>
                <AssetFilter
                  elmId={`${rootElmId}_mobile`}
                  onChangeHandler={filters => this.fetchAssets({ filters })}
                  filters={fetchParams.filters}
                  {...currentLocationAssetFilters}
                  mobileLayout
                />
              </div>
            </Drawer>
          </div>
        </Grid>
      </section>
    )
  }
}

export default EventPanel
